import { X } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { cn } from '@/lib/utils';
import type { GroceryItem } from '@/lib/grocery';

interface GroceryItemRowProps {
  item: GroceryItem;
  myPubkey: string;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
}

export function GroceryItemRow({ item, myPubkey, onToggle, onRemove }: GroceryItemRowProps) {
  const author = useAuthor(item.addedBy);
  const meta = author.data?.metadata;
  const isMine = item.addedBy === myPubkey;
  const adderName = isMine ? 'You' : (meta?.name ?? genUserName(item.addedBy));

  return (
    <li
      className={cn(
        'group relative flex items-center gap-3 rounded-xl px-3 py-2.5',
        'border border-transparent transition-all',
        'hover:bg-muted/50 hover:border-border/40',
        item.checked && 'opacity-55',
      )}
    >
      {/* Custom-styled checkbox; uses primary (Tokyo blue) ring when checked */}
      <Checkbox
        id={`item-${item.id}`}
        checked={item.checked}
        onCheckedChange={() => onToggle(item.id)}
        className={cn(
          'size-5 rounded-md border-2 shrink-0',
          'data-[state=checked]:bg-turquoise data-[state=checked]:border-turquoise',
          'data-[state=checked]:text-background',
          'transition-all',
        )}
        aria-label={`Mark ${item.name} as ${item.checked ? 'not done' : 'done'}`}
      />

      <label
        htmlFor={`item-${item.id}`}
        className="flex min-w-0 flex-1 items-baseline gap-2 cursor-pointer select-none"
      >
        <span
          className={cn(
            'truncate text-base font-medium',
            item.checked && 'line-through text-muted-foreground',
          )}
        >
          {item.name}
        </span>
        {item.qty && (
          <span
            className={cn(
              'text-xs font-mono shrink-0 px-1.5 py-0.5 rounded',
              'bg-turquoise/15 text-teal',
              item.checked && 'opacity-60',
            )}
          >
            {item.qty}
          </span>
        )}
      </label>

      {/* Adder avatar */}
      <Avatar
        className="size-6 shrink-0 ring-1 ring-border/60"
        title={`Added by ${adderName}`}
      >
        <AvatarImage src={sanitizeUrl(meta?.picture)} alt={adderName} />
        <AvatarFallback
          className={cn(
            'text-[9px] font-semibold',
            isMine ? 'bg-turquoise/20 text-teal' : 'bg-coral/20 text-coral-deep',
          )}
        >
          {adderName.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>

      {/* Remove button — only available for items the current user added.
          Items added by the partner can only be checked off (the partner
          owns their copy and would need to delete it themselves). */}
      {isMine ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onRemove(item.id)}
          className={cn(
            'size-7 shrink-0 rounded-md text-muted-foreground',
            'hover:bg-destructive/15 hover:text-destructive',
            'opacity-0 group-hover:opacity-100 focus:opacity-100',
            'transition-opacity',
          )}
          aria-label={`Remove ${item.name}`}
        >
          <X className="size-4" />
        </Button>
      ) : (
        <span className="size-7 shrink-0" aria-hidden />
      )}
    </li>
  );
}
